import { useEffect, useState } from "react"
import moment from "moment"
import { FeaturedProject } from "config"
import Address from "components/Address"
import ComposedOfDots from "components/ComposedOfDots"
import Loading from "components/Loading"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import PlatformBadgeRow from "components/PlatformBadgeRow"
import TokenFeatures from "components/TokenFeatures"
import { getProjectPlatformBadges, getProjectPlatformLinks } from "data/platforms"
import TokenProvenance from "components/TokenProvenance"
import useArtBlocksTokenMetadata from "hooks/useArtBlocksTokenMetadata"
import useCompositeSourceDots from "hooks/useCompositeSourceDots"
import usePageMeta from "hooks/usePageMeta"
import useTokenMetadata from "hooks/useTokenMetadata"
import useTokenProvenance from "hooks/useTokenProvenance"
import useWindowSize from "hooks/useWindowSize"
import {
  getArtBlocksImageUrl,
  getArtBlocksTokenUrl,
  getBlockExplorerName,
  getBlockExplorerTokenUrl,
  getOpenSeaAssetUrl,
} from "utils/artBlocksUrls"
import { getLiveGeneratorUrl } from "utils/liveGenerator"
import { getFeaturedProjectTokenIdForInvocation } from "utils/artBlocksMetadataApi"
import {
  canRenderTokenLive,
  getNftMetadataAspectRatio,
  getNftMetadataFeatures,
  getNftMetadataGeneratorUrl,
  getNftMetadataImageUrl,
} from "utils/tokenMetadata"
import { getArtworkFramePadding } from "utils/galleryLayout"

interface Props {
  project: FeaturedProject
  tokenId: string
}

const FeaturedTokenDetails = ({ project, tokenId }: Props) => {
  const windowSize = useWindowSize()
  const tokenResponse = useArtBlocksTokenMetadata(project, tokenId)
  const metadataResponse = useTokenMetadata(project.contractAddress, tokenId, project.chainId)
  const provenanceResponse = useTokenProvenance(project.contractAddress, tokenId, project.chainId)
  const token = tokenResponse.data
  const isDotsComposite = project.key === "dots-composites"
  const sourceDotsResponse = useCompositeSourceDots(token?.mint_transaction_hash, isDotsComposite)
  const nftMetadata = metadataResponse.data
  const metadataAspectRatio = getNftMetadataAspectRatio(nftMetadata)
  const aspectRatio = metadataAspectRatio || token?.project?.aspect_ratio || project.aspectRatio || 1
  const hasLiveView = canRenderTokenLive(nftMetadata, project.chainId)
  const [renderMode, setRenderMode] = useState<"image" | "live">("image")
  const invocation = token?.invocation
  const totalInvocations = token?.project?.invocations || 0
  const tokenTitle = typeof invocation === "number"
    ? `${project.name} #${invocation} — r4v3n`
    : `${project.name} — r4v3n`
  const ogImage = `https://media-proxy.artblocks.io/${project.chainId}/${project.contractAddress.toLowerCase()}/${tokenId}.png`
  usePageMeta(tokenTitle, undefined, ogImage, `${project.detailPath}/${tokenId}`)
  const hasPrevious = typeof invocation === "number" && invocation > 0
  const hasNext = typeof invocation === "number" && totalInvocations > 0 && invocation < totalInvocations - 1
  const previousTokenId = hasPrevious ? getFeaturedProjectTokenIdForInvocation(project, invocation - 1) : undefined
  const nextTokenId = hasNext ? getFeaturedProjectTokenIdForInvocation(project, invocation + 1) : undefined

  const artFramePadding = getArtworkFramePadding(windowSize.width)
  const artWidth =
    windowSize.width >= 1200
      ? (1200 - 48) * 0.62 - artFramePadding
      : windowSize.width >= 768
      ? (windowSize.width - 48) * 0.62 - artFramePadding
      : windowSize.width >= 640
      ? windowSize.width - 48 - artFramePadding
      : windowSize.width - 40 - artFramePadding
  const maxArtHeight = windowSize.height > 0 ? windowSize.height * 0.78 : undefined

  const tokenUrlParams = { chainId: project.chainId, contractAddress: project.contractAddress, tokenId }
  const generatorHref = getLiveGeneratorUrl({
    contractAddress: project.contractAddress,
    tokenId,
    chainId: project.chainId,
    metadataGeneratorUrl: getNftMetadataGeneratorUrl(nftMetadata),
    tokenHash: nftMetadata?.token_hash,
  })
  const imageHref = getNftMetadataImageUrl(nftMetadata) || getArtBlocksImageUrl(tokenUrlParams)
  const metadataHref = getArtBlocksTokenUrl(tokenUrlParams)
  const blockExplorerHref = getBlockExplorerTokenUrl(tokenUrlParams)
  const openSeaHref = getOpenSeaAssetUrl(tokenUrlParams)
  const platformBadges = getProjectPlatformBadges(project.contractAddress, project.projectId, {
    chainId: project.chainId,
    tokenId,
  })
  const platformLinks = getProjectPlatformLinks({
    contractAddress: project.contractAddress,
    projectId: project.projectId,
    tokenId,
    invocation: typeof invocation === "number" ? invocation : undefined,
    chainId: project.chainId,
  })
  const features = getNftMetadataFeatures(nftMetadata)
  const loading = tokenResponse.loading || metadataResponse.loading
  const ownerAddress = provenanceResponse.data?.owner_address || token?.owner_address

  useEffect(() => {
    if (!tokenId || metadataResponse.loading) return
    setRenderMode(hasLiveView ? "live" : "image")
  }, [hasLiveView, metadataResponse.loading, tokenId])

  if (tokenResponse.error && metadataResponse.error) {
    return <p className="text-sm text-red-500">Error loading token.</p>
  }

  return (
    <div>
      <nav className="flex items-center gap-2 mb-10" aria-label="Breadcrumb">
        <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          Work
        </a>
        <span className="text-gallery-border">·</span>
        <a href={project.detailPath} className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          {project.name}
        </a>
        <span className="text-gallery-border">·</span>
        <span className="gallery-label text-gallery-text">
          {typeof invocation === "number" ? `No. ${invocation}` : tokenId}
        </span>
      </nav>

      {loading && !nftMetadata && !token && <Loading />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
        <div className="md:col-span-2">
          <div className="gallery-art-shell">
            <div className="gallery-art-frame">
              <MetadataAwareTokenView
                project={project}
                tokenId={tokenId}
                width={Math.floor(Math.max(180, artWidth))}
                fallbackAspectRatio={aspectRatio}
                maxHeight={maxArtHeight}
                live={renderMode === "live"}
                metadata={nftMetadata}
                metadataLoading={metadataResponse.loading}
                loupe
                loupeZoom={3}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 pt-4 border-t border-gallery-border">
            <div className="flex items-center gap-4">
              {previousTokenId && (
                <a
                  href={`${project.detailPath}/${previousTokenId}`}
                  className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
                >
                  Prev
                </a>
              )}
              {nextTokenId && (
                <a
                  href={`${project.detailPath}/${nextTokenId}`}
                  className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
                >
                  Next
                </a>
              )}
            </div>
            <div className="flex flex-col items-start sm:flex-row sm:items-center gap-4">
              <div className="gallery-segment-group" aria-label="Render mode">
                <button
                  type="button"
                  aria-pressed={renderMode === "image"}
                  className="gallery-segment-button"
                  onClick={() => setRenderMode("image")}
                >
                  Image
                </button>
                <button
                  type="button"
                  aria-pressed={renderMode === "live"}
                  disabled={!hasLiveView}
                  className="gallery-segment-button"
                  onClick={() => setRenderMode("live")}
                >
                  Live
                </button>
              </div>
              {generatorHref && (
                <a
                  href={generatorHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
                >
                  Live view
                </a>
              )}
              <a
                href={imageHref}
                target="_blank"
                rel="noopener noreferrer"
                className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
              >
                Image
              </a>
            </div>
          </div>
        </div>

        <aside className="md:col-span-1">
          <p className="gallery-label text-gallery-muted mb-3">
            {token?.minted_at ? moment(token.minted_at).format("LL") : project.chainName}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight font-normal mb-3">
            {nftMetadata?.name || `${project.name} ${typeof invocation === "number" ? `#${invocation}` : tokenId}`}
          </h1>
          <p className="artist-name text-gallery-muted mb-6">{project.artistName}</p>

          <PlatformBadgeRow badges={platformBadges} className="mb-8" />

          <dl className="gallery-meta-grid mb-8">
            <div className="gallery-meta-cell col-span-2">
              <dt>Token ID</dt>
              <dd className="break-all">{tokenId}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Invocation</dt>
              <dd>{typeof invocation === "number" ? `#${invocation}` : "..."}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Aspect</dt>
              <dd>{aspectRatio.toFixed(2)}</dd>
            </div>
            {ownerAddress && (
              <div className="gallery-meta-cell col-span-2">
                <dt>Owner</dt>
                <dd><Address address={ownerAddress} /></dd>
              </div>
            )}
          </dl>

          <div className="flex flex-col gap-2 mb-8">
            {platformLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="gallery-label text-gallery-text hover:text-gallery-accent transition-colors"
              >
                {link.label} →
              </a>
            ))}
            <a
              href={metadataHref}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
            >
              Metadata
            </a>
            <a
              href={blockExplorerHref}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
            >
              {getBlockExplorerName(project.chainId)}
            </a>
            <a
              href={openSeaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
            >
              OpenSea
            </a>
          </div>

          {isDotsComposite && (
            <ComposedOfDots
              sourceDots={sourceDotsResponse.data}
              loading={sourceDotsResponse.loading}
              error={sourceDotsResponse.error}
            />
          )}

          <TokenFeatures features={features} collectionHref={project.detailPath} />

          <TokenProvenance
            transfers={provenanceResponse.data?.transfers}
            chainId={project.chainId}
            loading={provenanceResponse.loading}
            error={provenanceResponse.error}
          />
        </aside>
      </div>
    </div>
  )
}

export default FeaturedTokenDetails
